import { DashboardLayout } from "../DashboardLayout";

export const ShimmerLoader = () => {
  return (
    <div className="p-8 w-full">
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-6 bg-gray-300 rounded animate-pulse w-full" />
        ))}
      </div>
    </div>
  );
};

export const NavbarShimmer = () => (
  <div className="animate-pulse">
    <div className="h-8 bg-gray-200 rounded w-1/4 mb-2"></div>
    <div className="h-4 bg-gray-200 rounded w-1/3"></div>
  </div>
);

export const ButtonShimmer = ({ width = "w-32" }: { width?: string }) => (
  <div className={`h-10 bg-gray-200 rounded ${width} animate-pulse`}></div>
);

export const TabShimmer = () => (
  <div className="flex gap-4 mb-6 border-b border-gray-200">
    {[1, 2, 3, 4].map((item) => (
      <div key={item} className="pb-2 px-1">
        <div className="h-6 bg-gray-200 rounded w-24 animate-pulse"></div>
      </div>
    ))}
  </div>
);

export const SearchBarShimmer = () => (
  <div className="bg-gray-100 px-4 py-3 mt-8 rounded-md animate-pulse">
    <div className="flex gap-3 flex-col md:flex-row md:justify-between md:items-center">
      <div className="flex-1">
        <div className="h-10 bg-gray-200 rounded w-full"></div>
      </div>
      <div>
        <div className="h-10 bg-gray-200 rounded w-32"></div>
      </div>
    </div>
  </div>
);

export const TableRowShimmer = () => (
  <div className="space-y-3">
    {[1, 2, 3, 4, 5].map((row) => (
      <div key={row} className="bg-white p-4 rounded-lg shadow-sm">
        <div className="flex justify-between items-start">
          <div className="space-y-2 flex-1">
            <div className="h-5 bg-gray-200 rounded w-3/4 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse"></div>
            <div className="flex gap-2 mt-3">
              <div className="h-6 bg-gray-200 rounded w-16 animate-pulse"></div>
              <div className="h-6 bg-gray-200 rounded w-20 animate-pulse"></div>
              <div className="h-6 bg-gray-200 rounded w-24 animate-pulse"></div>
            </div>
          </div>
          <div className="ml-4">
            <div className="h-8 bg-gray-200 rounded w-20 animate-pulse"></div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

export const SuggestedJobsShimmer = () => (
  <div className="space-y-4">
    {[1, 2, 3].map((item) => (
      <div key={item} className="bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 bg-gray-200 rounded-full animate-pulse"></div>
          <div className="space-y-2 flex-1">
            <div className="h-5 bg-gray-200 rounded w-2/3 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-1/3 animate-pulse"></div>
          </div>
        </div>
        <div className="h-4 bg-gray-200 rounded w-full mb-2 animate-pulse"></div>
        <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div>
        <div className="flex gap-2 mt-4">
          <div className="h-6 bg-gray-200 rounded w-16 animate-pulse"></div>
          <div className="h-6 bg-gray-200 rounded w-20 animate-pulse"></div>
        </div>
      </div>
    ))}
  </div>
);

export const DashboardShimmer = () => {
  return (
    <div className="space-y-10">

      {/* Top visibility / profile section */}
      <div className="bg-white rounded-2xl p-8 border border-gray-200">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Circle + text */}
          <div className="flex flex-col items-center">
            <div className="w-56 h-56 bg-gray-200 rounded-full animate-pulse mb-6" />
            <div className="h-6 bg-gray-200 rounded w-64 animate-pulse mb-3" />
            <div className="h-4 bg-gray-200 rounded w-80 animate-pulse" />
          </div>

          {/* Categories */}
          <div>
            <div className="h-6 bg-gray-200 rounded w-72 animate-pulse mb-6" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="h-12 bg-gray-200 rounded-xl animate-pulse"
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="bg-white rounded-xl p-6 border border-gray-200 space-y-4"
          >
            <div className="h-5 bg-gray-200 rounded w-32 animate-pulse" />
            <div className="h-10 bg-gray-200 rounded w-20 animate-pulse" />
          </div>
        ))}
      </div>

      {/* Jobs + Suggestions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-2xl p-6 border border-gray-200">
          <div className="h-6 bg-gray-200 rounded w-40 animate-pulse mb-6" />
          <div className="h-10 bg-gray-200 rounded w-full animate-pulse mb-6" />
          <TableRowShimmer />
        </div>

        <SuggestedJobsShimmer />
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {[1, 2].map((i) => (
          <div
            key={i}
            className="bg-white rounded-lg p-6 border border-gray-200"
          >
            <div className="h-6 bg-gray-200 rounded w-40 animate-pulse mb-6" />
            <ShimmerLoader />
          </div>
        ))}
      </div>
    </div>
  );
};

export const TalentJobsShimmer = () => {
  return (
    <DashboardLayout>
      <div className="animate-pulse">

        <div className="mb-6">
          <div className="h-8 bg-gray-200 rounded w-64 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-48"></div>
        </div>

        <div className="flex gap-6 mt-6">

          <div className="w-56 bg-white border border-gray-200 rounded-xl p-4 space-y-2">
            {[...Array(4)].map((_, index) => (
              <div
                key={index}
                className="w-full h-10 bg-gray-200 rounded-lg"
              ></div>
            ))}
          </div>


          <div className="flex-1">

            <div className="flex justify-between items-center bg-white border border-gray-200 rounded-xl px-4 py-3 mb-6 shadow-sm">
              <div className="relative w-96">
                <div className="h-10 bg-gray-200 rounded-lg w-full"></div>
              </div>
              <div className="h-10 bg-gray-200 rounded-lg w-32"></div>
            </div>

            {/* Job Cards Shimmer */}
            <div className="grid grid-cols-1 gap-6">
              {[...Array(4)].map((_, index) => (
                <JobCardShimmer key={index} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

// Job Card Shimmer component
export const JobCardShimmer = () => {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 animate-pulse">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>

          <div className="flex gap-2 mb-4">
            <div className="h-6 bg-gray-200 rounded w-20"></div>
            <div className="h-6 bg-gray-200 rounded w-24"></div>
            <div className="h-6 bg-gray-200 rounded w-16"></div>
          </div>

          <div className="space-y-2 mb-4">
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
          </div>
        </div>

        <div className="flex flex-col items-end ml-4">
          <div className="w-8 h-8 bg-gray-200 rounded-full mb-4"></div>
          <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
        </div>
      </div>

      <div className="flex justify-between items-center pt-4 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
          <div>
            <div className="h-4 bg-gray-200 rounded w-32 mb-1"></div>
            <div className="h-3 bg-gray-200 rounded w-24"></div>
          </div>
        </div>

        <div className="flex gap-2">
          <div className="h-10 bg-gray-200 rounded w-24"></div>
          <div className="h-10 bg-gray-200 rounded w-20"></div>
        </div>
      </div>
    </div>
  );
};


export const FilterJobShimmer = () => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-end animate-pulse">
      <div className="w-96 bg-white h-full p-6">
        <div className="flex justify-between items-center mb-8">
          <div className="h-6 bg-gray-200 rounded w-32"></div>
          <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
        </div>

        <div className="space-y-8">
          {[...Array(5)].map((_, index) => (
            <div key={index}>
              <div className="h-5 bg-gray-200 rounded w-40 mb-4"></div>
              <div className="space-y-2">
                {[...Array(3)].map((_, idx) => (
                  <div key={idx} className="flex items-center">
                    <div className="w-5 h-5 bg-gray-200 rounded mr-3"></div>
                    <div className="h-4 bg-gray-200 rounded w-32"></div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="flex gap-3">
            <div className="h-12 bg-gray-200 rounded flex-1"></div>
            <div className="h-12 bg-gray-200 rounded flex-1"></div>
          </div>
        </div>
      </div>
    </div>
  );
};


export const EmptyJobsShimmer = () => {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-12 text-center animate-pulse">
      <div className="mx-auto w-24 h-24 bg-gray-200 rounded-full mb-6"></div>
      <div className="h-6 bg-gray-200 rounded w-64 mx-auto mb-3"></div>
      <div className="h-4 bg-gray-200 rounded w-96 mx-auto mb-8"></div>
      <div className="h-12 bg-gray-200 rounded w-48 mx-auto"></div>
    </div>
  );
};


export const TalentJobsLoading = () => {
  return <TalentJobsShimmer />;
};

export const JobDetailsShimmer = () => {
  return (
    <DashboardLayout>
      {/* Navbar shimmer */}
      <div className="mb-6">
        <div className="h-8 bg-gray-200 rounded w-48 mb-2 animate-pulse" />
        <div className="h-4 bg-gray-200 rounded w-64 animate-pulse" />
      </div>

      {/* Breadcrumb shimmer */}
      <div className="flex gap-2 mb-6">
        <div className="h-4 bg-gray-200 rounded w-20 animate-pulse" />
        <div className="h-4 bg-gray-200 rounded w-32 animate-pulse" />
        <div className="h-4 bg-gray-200 rounded w-40 animate-pulse" />
      </div>

      <div className="grid grid-cols-1 gap-6">
        {/* Job summary card */}
        <div className="bg-white rounded-lg shadow-md p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/2 mb-3" />
          <div className="h-6 bg-gray-200 rounded w-40 mb-6" />

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gray-200 rounded-full" />
                <div className="space-y-2">
                  <div className="h-3 bg-gray-200 rounded w-20" />
                  <div className="h-4 bg-gray-200 rounded w-24" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Description */}
        <div className="bg-white rounded-lg shadow-md p-6 animate-pulse">
          <div className="h-5 bg-gray-200 rounded w-40 mb-4" />
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded w-full" />
            <div className="h-4 bg-gray-200 rounded w-5/6" />
            <div className="h-4 bg-gray-200 rounded w-4/6" />
          </div>
        </div>

        {/* Skills */}
        <div className="bg-white rounded-lg shadow-md p-6 animate-pulse">
          <div className="h-5 bg-gray-200 rounded w-32 mb-4" />
          <div className="flex flex-wrap gap-3">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="h-8 bg-gray-200 rounded w-24"
              />
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};
